/**
 * ItemizedSummaryBar — T146 + T146P2 (US2)
 *
 * 2-segment progress bar with density-aware display formats.
 * Shows itemized vs unitemized portion of a transaction's amount.
 *
 * Density rendering:
 * - Compact: "${items.length} items, $${total}"
 * - Receipt (default): 2-segment progress bar + labels
 * - Detailed: "${items.length} items @ ${avg} / item"
 *
 * Pattern: shadcn/ui (Tailwind only) — no new CSS files.
 * @see https://tailwindcss.com/docs/flex
 */
import { cn } from '@/lib/utils';
import { useCurrency } from '@/hooks/useCurrency';

interface ItemizedSummaryBarProps {
  itemizedTotal: number;
  transactionTotal: number;
  itemSubtotal?: number;
  itemDiscountTotal?: number;
  transactionDiscountTotal?: number;
  /** When false, omit the Net Total label to avoid duplicate net copy in parent contexts. */
  showNetTotal?: boolean;
  /** ISO 4217 currency code (passed for parity, fmt resolves from auth store). */
  currency?: string;
  /** T146P2: Display density (compact|receipt|detailed) */
  density?: 'compact' | 'receipt' | 'detailed';
  /** T146P2: Number of items (for compact/detailed modes) */
  itemCount?: number;
  className?: string;
}

export default function ItemizedSummaryBar({
  itemizedTotal,
  transactionTotal,
  itemSubtotal,
  itemDiscountTotal,
  transactionDiscountTotal,
  showNetTotal = true,
  currency,
  className,
  density = 'receipt',
  itemCount = 0,
}: ItemizedSummaryBarProps) {
  const { currency: preferredCurrency } = useCurrency();
  const activeCurrency = currency && currency.length === 3 ? currency : preferredCurrency;

  const formatSummaryMoney = (value: number) =>
    new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: activeCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number.isFinite(value) ? value : 0);

  // T146/T205: Guard against zero/negative totals and support discount-aware summaries.
  const safeTotal = Math.max(0, Math.abs(transactionTotal));
  const safeItemized = Math.max(0, itemizedTotal);
  const safeItemSubtotal = Math.max(0, itemSubtotal ?? safeItemized);
  const safeItemDiscount = Math.max(0, itemDiscountTotal ?? Math.max(0, safeItemSubtotal - safeItemized));
  const safeTransactionDiscount = Math.max(0, transactionDiscountTotal ?? 0);
  const safeTransactionNet = Math.max(0, safeTotal - safeTransactionDiscount);
  const unitemized = Math.max(0, safeTransactionNet - safeItemized);
  // Cap visual fill at 100% even if items overshoot (validation surface lives in the form).
  const itemizedPct = safeTransactionNet > 0 ? Math.min(100, (safeItemized / safeTransactionNet) * 100) : 0;
  const unitemizedPct = Math.max(0, 100 - itemizedPct);
  const fullyItemized = safeTransactionNet > 0 && safeItemized >= safeTransactionNet - 0.01;
  const avgPerItem = itemCount > 0 ? safeItemized / itemCount : 0;

  // T146P2: Compact density
  if (density === 'compact') {
    return (
      <div data-testid="itemized-summary-bar" className={cn('text-xs text-muted-foreground', className)}>
        {itemCount} items, {formatSummaryMoney(safeItemized)} net
      </div>
    );
  }

  // T146P2: Detailed density
  if (density === 'detailed') {
    return (
      <div data-testid="itemized-summary-bar" className={cn('text-xs text-muted-foreground', className)}>
        {itemCount} items @ {formatSummaryMoney(avgPerItem)} net / item
      </div>
    );
  }

  // Receipt (default): 2-segment progress bar
  return (
    <div
      data-testid="itemized-summary-bar"
      className={cn('flex flex-col gap-1.5', className)}
    >
      {/* T146: 2-segment bar — primary fill + muted remainder */}
      <div
        className="h-2 rounded-full bg-muted overflow-hidden flex"
        role="progressbar"
        aria-label={`Itemization: ${Math.round(itemizedPct)}% covered`}
        aria-valuenow={Math.round(itemizedPct)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          data-testid="itemized-progress-fill"
          className={cn('bg-primary transition-all duration-200', fullyItemized && 'bg-emerald-500')}
          style={{ width: `${itemizedPct}%` }}
        />
        {unitemized > 0 && (
          <div
            className="bg-muted-foreground/20"
            style={{ width: `${unitemizedPct}%` }}
          />
        )}
      </div>

      {/* T146: Labels row — itemized | unitemized | total */}
      <div className="flex items-center justify-between text-xs gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className={cn(
              'inline-block w-2 h-2 rounded-full',
              fullyItemized ? 'bg-emerald-500' : 'bg-primary',
            )}
          />
          {fullyItemized ? (
            <span className="font-medium text-emerald-600 dark:text-emerald-500">
              Fully itemized
            </span>
          ) : (
            <span>
              Itemized:{' '}
              <span className="tabular-nums font-medium">{formatSummaryMoney(safeItemized)}</span>
            </span>
          )}
        </span>

        {unitemized > 0 && (
          <span className="inline-flex items-center gap-3">
            <span className="text-muted-foreground">
              Unitemized:{' '}
              <span className="tabular-nums font-medium">{formatSummaryMoney(unitemized)}</span>
            </span>
          </span>
        )}
      </div>

      {showNetTotal && (
        <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
          <span>Gross Subtotal: <span className="font-medium text-foreground tabular-nums">{formatSummaryMoney(safeItemSubtotal)}</span></span>
          <span>Discounts Subtracted: <span className="font-medium text-foreground tabular-nums">-{formatSummaryMoney(safeItemDiscount + safeTransactionDiscount)}</span></span>
          <span>Net Total: <span className="font-medium text-foreground tabular-nums" data-testid="summary-net-total-value">{formatSummaryMoney(safeTransactionNet)}</span></span>
        </div>
      )}
    </div>
  );
}
